'use client';

import type { ConversationDto } from '@shared/all/domains/messages';
import { buildAvatarUrl } from '@shared/all/helpers/avatarUrl';
import { API_URL } from '@shared/utils/helpers/config';
import { unwrapFromResult } from '@shared/utils/helpers/resultWrapper';
import { getS3ObjectURL } from '@shared/utils/helpers/s3';
import toast from '@shared/utils/helpers/toast';
import useAuth from '@shared/utils/hooks/useAuth';
import { useConversations } from '@shared/utils/hooks/useConversations';
import useFactiiis from '@shared/utils/hooks/useFactiiis';
import useNavigator from '@shared/utils/hooks/useNavigator';
import useSocket, { type ActivityStatus, type NewConversationPayload } from '@shared/utils/hooks/useSocket';
import { useSocketAuthRecovery } from '@shared/utils/hooks/useSocketAuthRecovery';
import useUser from '@shared/utils/hooks/useUser';
import socket from '@shared/utils/socket/socket';
import { useInfiniteQuery, useIsFetching, useQueryClient } from '@tanstack/react-query';
import dynamic from 'next/dynamic';
import { type PropsWithChildren, startTransition, useEffect, useRef, useState } from 'react';
import { Toaster } from 'react-hot-toast';
import { UNREAD_MESSAGES_KEY } from '../../constants/queryKeys';
import eventEmitter from '../../helpers/eventEmitter';
import { TERMINAL_WINDOW_PATH } from '../../helpers/terminalWindow';
import { useLocationPathname, useLocationSearchParam } from '../../hooks/useLocationSearchParam';
import usePresence from '../../hooks/usePresence';
import { useShouldShowOnboardingOverlay } from '../../utils/onboardingStorage';
import { client, service } from '../../utils/trpc';
import { VerificationSessionScreen } from '../verification/SessionScreen';
import OnboardingGate from './OnboardingGate';
import { Splash } from './Splash';

// Upper bound on the loading splash. Not the normal dismissal path — see the
// safety-net effect in Main.
const SPLASH_MAX_MS = 8000;

// The chat workspace (rail, DM/channel sidebar, thread) — rendered globally so
// it works on all pages, and so going full screen keeps the same mount.
const ChatWorkspace = dynamic(() => import('../pages/chat/ChatWorkspace'), {
  ssr: false
});
export const Main: React.FC<PropsWithChildren> = ({
  children
}) => {
  // Not useSearchParams: that would make the root layout's Suspense boundary
  // a dynamic hole and push every page's content out of the prerendered HTML
  // (see useLocationSearchParam). The server and the hydrating render see no
  // mode, so an embed WebView shows the splash until hydration, then drops
  // the chrome on the very next render.
  const mode = useLocationSearchParam('mode');
  const isEmbed = mode === 'embed' || mode === 'compact' || mode === 'mobile';
  // A window, not a page: no chat rail or popups over a shell you type into.
  // Keeps the toaster — that is where its errors go. Read from the window,
  // not usePathname: the terminal is only ever opened by window.open, never
  // reached by a soft navigation, and a usePathname here kept the whole shell
  // request-bound on every dynamic route.
  const isWindow = useLocationPathname() === TERMINAL_WINDOW_PATH;
  const [splash, setSplash] = useState<'loading' | 'maintenance' | null>('loading');
  // Keeps the splash mounted through its fade-out (was AnimatePresence). By
  // then `splash` is already null, so the last mode is retained for the
  // outgoing frame.
  const splashPresence = usePresence(splash !== null, 500);
  // Whether any query has actually been in flight yet — see the splash effect.
  const startedFetching = useRef(false);
  const lastSplashMode = useRef(splash);
  if (splash) lastSplashMode.current = splash;
  const splashMode = splash ?? lastSplashMode.current;
  const isFetching = useIsFetching();
  const queryClient = useQueryClient();
  const {
    resetConversations
  } = useConversations();
  const {
    currentUser,
    initializeUser,
    refreshUserFromCookie,
    logout
  } = useAuth({
    client,
    service,
    onLogout: resetConversations
  });
  // Same predicate OnboardingGate renders on, so the floating widgets hide only
  // while the overlay is actually up (embed mode already early-returns below).
  const isOnboarding = useShouldShowOnboardingOverlay(currentUser);
  const {
    setIsOnline
  } = useNavigator();
  const {
    initializeFactiiis
  } = useFactiiis(client);
  const {
    addTrons,
    setPendingNotificationsCount,
    setIsAuthenticated
  } = useUser();
  const {
    onNewMessage,
    setUserTyping,
    setConversations,
    setError,
    setLoading,
    setHasNextPage,
    setFetchNextPage,
    updateActivityStatus,
    addConversations,
    removeConversation,
    rejectDisputeConversation,
    handleMemberRemoved,
    updateMessage,
    markMessageDeleted
  } = useConversations();
  const {
    onMessage,
    onRefreshUser,
    onTronReward,
    onLogout,
    onNotificationCount,
    onActivity,
    onConversationDeleted,
    onBanDisputeRejected,
    onMemberRemoved,
    onTyping,
    onNewMessage: onNewMessageSocket,
    onMessageEdited,
    onMessageDeleted,
    onNewConversation,
    onOnedriveChanged,
    registerOnedriveWebhook
  } = useSocket();

  //load conversations at top level so that they are always available in case of floating conversations
  const {
    data,
    hasNextPage,
    error,
    fetchNextPage,
    isLoading
  } = useInfiniteQuery({
    queryKey: ['conversations'],
    queryFn: async ({
      pageParam
    }) => unwrapFromResult(client.messages.get.query({
      cursor: pageParam
    })),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: lastPage => {
      const conversations = lastPage?.conversations ?? [];
      if (conversations.length < 10) return undefined;
      return conversations[conversations.length - 1]?.id;
    },
    enabled: Boolean(currentUser.isAuthenticated)
  });

  // Copy conversations from query to state
  useEffect(() => {
    if (data) {
      setConversations(data.pages.flatMap(p => p.conversations));
    }
    setHasNextPage(hasNextPage);
    setError(error);
    setLoading(isLoading);
    setFetchNextPage(fetchNextPage);
  }, [data, hasNextPage, error, fetchNextPage, isLoading]);

  // Non-socket initialization
  useEffect(() => {
    const eventLogoutListener = () => {
      logout(true);
    };
    const cookieRotatedListener = () => {
      void refreshUserFromCookie();
    };
    eventEmitter.on('logout', eventLogoutListener);
    eventEmitter.on('setOnline', () => setIsOnline(true));
    eventEmitter.on('userCookieRotated', cookieRotatedListener);

    // Every first render starts signed-out-unknown (Provider seeds no user, so
    // SSR and hydration agree). A fresh client cookie is the same answer the
    // server would give, so it is applied here without a round trip — the
    // state the cookie-seeded first render used to start in. Only a missing or
    // unreadable cookie asks the server.
    //
    // As a transition: this runs before the streamed Suspense boundaries (the
    // header, a page's content) have hydrated. An urgent update would make
    // React hydrate them against the signed-in state and throw a hydration
    // mismatch ("your-spaces" vs "suggested", avatar img vs placeholder); a
    // transition lets them hydrate as rendered first, then applies the user.
    // On web refreshUserFromCookie dispatches synchronously, so the outer
    // transition covers it; the inner one covers the update after the await.
    if (currentUser.isAuthenticated === null) {
      startTransition(() => {
        void refreshUserFromCookie().then(fromCookie => {
          startTransition(() => {
            if (fromCookie) setIsAuthenticated(true);else void initializeUser();
          });
        });
      });
    }
    void initializeFactiiis();
    return () => {
      eventEmitter.off('logout', eventLogoutListener);
      eventEmitter.off('userCookieRotated', cookieRotatedListener);
    };
  }, []);

  // The handshake rejects a stale token now, and socket.io calls a middleware
  // error fatal, so something has to refresh and reconnect. Shared with mobile.
  useSocketAuthRecovery(client);

  // Socket handlers — re-initialized on auth change so closures stay fresh.
  // Anonymous visitors connect too: the server lets any socket join
  // `post-<id>` rooms (joinPostStats), which is how a logged-out reader sees
  // live vote counts.
  useEffect(() => {
    if (currentUser.isAuthenticated === null) return;
    service.refreshIfNeeded().then(() => socket.connect());
    const cleanupMessage = onMessage((message: string) => {
      toast(message);
    });
    const cleanupRefreshUser = onRefreshUser(() => {
      initializeUser();
    });
    const cleanupTronReward = onTronReward((quantity: number, title: string) => {
      addTrons(quantity);
      toast(`You earned ${quantity.toLocaleString()} Trons for: ${title}!`);
    });
    const cleanupLogout = onLogout(() => {
      logout(true);
    });
    const cleanupNotificationCount = onNotificationCount((count, detail) => {
      setPendingNotificationsCount(count);
      // Native Electron notification
      if (window.electronAPI?.isElectron) {
        window.electronAPI.setBadgeCount(count);
        if (count > 0 && !document.hasFocus() && detail) {
          const messages: Record<string, string> = {
            UPVOTED: `${detail.username} upvoted your post`,
            FOLLOWED: `${detail.username} followed you`,
            REPLIED: `${detail.username} replied to your post`,
            TAGGED_POST: `${detail.username} tagged you in a post`,
            SPACE_INVITES: `${detail.username} invited you to a space`,
            SPACE_BANNED: `You were banned from a space`,
            SPACE_DELISTED: `Your space was delisted`,
            FACTIII_MODERATION_REQUIRED: `Your factiii needs moderation`,
            FACTIII_MODERATION_APPROVED: `Your factiii was approved`,
            FACTIII_MODERATION_REJECTED: `Your factiii was rejected`
          };
          const body = messages[detail.type] ?? `You have a new notification`;
          window.electronAPI.showNotification('Factiii', body);
        }
      }
    });

    // OneDrive index push (desktop only): relay change pokes and mint webhooks
    // for the shell. Gate on the namespace, not on isElectron - shells older
    // than 1.6.0 don't expose `onedrive` and would crash the app here.
    let cleanupOnedrive = () => {};
    if (window.electronAPI?.onedrive) {
      const api = window.electronAPI.onedrive;
      const offChanged = onOnedriveChanged(spaceSlug => api.changed(spaceSlug));
      const offMint = api.onMintRequest(spaceSlug_0 => {
        void registerOnedriveWebhook(spaceSlug_0).then(reg => {
          // Callback URL is the API origin this app talks to; Graph must be able
          // to reach it, so a localhost dev origin simply never gets a poke and
          // the index falls back to its delta pass.
          const base = process.env.NEXT_PUBLIC_API_URL;
          if (!reg || !base) return;
          api.registerWebhook({
            spaceSlug: spaceSlug_0,
            notificationUrl: `${base.replace(/\/$/, '')}/onedrive/webhook`,
            clientState: reg.clientState
          });
        });
      });
      cleanupOnedrive = () => {
        offChanged();
        offMint();
      };
    }
    const cleanupTyping = onTyping((convId: string, senderId: number) => {
      setUserTyping(convId, senderId);
    });
    const cleanupNewMessage = onNewMessageSocket((sender, convId_0, message_0, messageId, createdAt, attachments, parentId) => {
      // Thread replies belong to their thread pane, which subscribes
      // separately. Letting them through here would drop replies into the
      // main transcript and re-mark the conversation unread — the two things
      // threads exist to avoid.
      if (parentId) return;
      const senderWithActivity = {
        ...sender,
        lastActive: new Date(),
        isActive: true
      };
      onNewMessage(senderWithActivity, convId_0, message_0, messageId, createdAt, attachments);
      // A conversation this account has not loaded can still go unread, so
      // the header's badge is re-counted server-side rather than derived
      // from the conversations already in context.
      void queryClient.invalidateQueries({
        queryKey: UNREAD_MESSAGES_KEY
      });
      // Native Electron notification for new messages with avatar.
      //
      // `sender.id` is checked because the server broadcasts `newMessage` to
      // the WHOLE conversation room with no `.except()` — deliberately, since
      // this account's other devices need it to keep their transcript in
      // sync. Without the check, sending from the phone popped a desktop
      // notification announcing your own message back to you.
      if (window.electronAPI?.isElectron && !document.hasFocus() && sender.id !== currentUser.id) {
        // A raster, because this ends up as an OS notification icon — macOS
        // and Windows both want a bitmap, not a vector.
        const avatarUrl = sender.avatarKey ? getS3ObjectURL(sender.avatarKey) : sender.robohash ? buildAvatarUrl(API_URL, sender.robohash, {
          format: 'png',
          size: 128
        }) : undefined;
        window.electronAPI.showNotification(sender.name || sender.username, message_0, avatarUrl);
      }
    });
    const cleanupMessageEdited = onMessageEdited((messageId_0, convId_1, content) => {
      updateMessage(convId_1, messageId_0, content);
    });
    const cleanupMessageDeleted = onMessageDeleted((messageId_1, convId_2) => {
      markMessageDeleted(convId_2, messageId_1);
    });
    const cleanupNewConversation = onNewConversation((data_0: NewConversationPayload) => {
      const conversationDto: ConversationDto = {
        ...data_0,
        type: data_0.type ?? 'REGULAR',
        ban: data_0.ban ?? null,
        // Brand new: nothing unread, cannot already be muted.
        unreadCount: data_0.unreadCount ?? 0,
        muted: data_0.muted ?? false,
        // Channels never arrive over newConversation (joining is a mutation
        // that returns the full DTO), so channel metadata defaults to null.
        kind: data_0.kind ?? 'DM',
        slug: null,
        topic: null,
        isPublic: true,
        space: null,
        factiii: null,
        participants: data_0.participants.map(p_0 => ({
          ...p_0,
          lastActive: p_0.lastActive ?? new Date(),
          isActive: p_0.isActive
        }))
      };
      addConversations([conversationDto]);
    });
    const cleanupActivity = onActivity((status: ActivityStatus, convId_3: string) => {
      if (status.lastActive) {
        updateActivityStatus(status.userId, status.lastActive, status.isActive, convId_3);
      }
    });
    const cleanupConversationDeleted = onConversationDeleted((conversationId: string) => {
      removeConversation(conversationId);
    });
    const cleanupBanDisputeRejected = onBanDisputeRejected((conversationId_0: string) => {
      rejectDisputeConversation(conversationId_0);
    });
    const cleanupMemberRemoved = onMemberRemoved((conversationId_1: string, memberId: number) => {
      handleMemberRemoved(conversationId_1, memberId, currentUser.id);
    });
    return () => {
      socket.disconnect();
      cleanupMessage();
      cleanupRefreshUser();
      cleanupTronReward();
      cleanupLogout();
      cleanupNotificationCount();
      cleanupOnedrive();
      cleanupTyping();
      cleanupNewMessage();
      cleanupMessageEdited();
      cleanupMessageDeleted();
      cleanupNewConversation();
      cleanupActivity();
      cleanupConversationDeleted();
      cleanupBanDisputeRejected();
      cleanupMemberRemoved();
    };
  }, [currentUser.isAuthenticated]);

  // Safety net only: every real load fires at least one query, so the effect
  // below dismisses long before this. It exists so a pathological load where
  // nothing ever fetches can't strand the user on the splash forever.
  useEffect(() => {
    const timer = setTimeout(() => {
      startedFetching.current = true;
      setSplash(prev => prev === 'loading' ? null : prev);
    }, SPLASH_MAX_MS);
    return () => clearTimeout(timer);
  }, []);

  // Splash: hide when loaded, switch to maintenance if server is unreachable
  useEffect(() => {
    // `isFetching === 0` is true in two opposite situations: after everything
    // settles, and before anything starts. At boot it is the second — every
    // query waits on auth.refresh, which is a raw client call rather than a
    // React Query one and so never registers here. Dismissing on that zero
    // dropped the splash mid-flight and left a bare shell on screen until the
    // batch landed, so the counter has to rise once before zero means "done".
    if (isFetching > 0) startedFetching.current = true;
    if (currentUser.isAuthenticated !== null && isFetching === 0 && startedFetching.current) {
      // Dismiss splash whether it's in loading or maintenance (recovered)
      setSplash(prev_0 => prev_0 ? null : prev_0);
    }
    const handleOffline = () => {
      if (splash === 'loading') {
        setSplash('maintenance');
      } else {
        setIsOnline(false);
      }
    };

    // Recover from maintenance splash when server comes back online
    const handleOnline = () => {
      setSplash(prev_1 => prev_1 === 'maintenance' ? null : prev_1);
    };
    eventEmitter.on('setOffline', handleOffline);
    eventEmitter.on('setOnline', handleOnline);
    return () => {
      eventEmitter.off('setOffline', handleOffline);
      eventEmitter.off('setOnline', handleOnline);
    };
  }, [currentUser.isAuthenticated, isFetching, splash]);
  if (isEmbed) return <>{children}</>;
  return <>
      {splashPresence.rendered && splashMode && <Splash mode={splashMode} exiting={splashPresence.exiting} onRetry={splashMode === 'maintenance' ? () => window.location.reload() : undefined} />}
      <OnboardingGate />
      {children}
      {!isOnboarding && !isWindow && <ChatWorkspace />}
      <VerificationSessionScreen />
      <Toaster position="bottom-center" toastOptions={{
      duration: 3000,
      style: {
        fontFamily: 'Lato, sans-serif',
        fontSize: '15px',
        borderRadius: '10px',
        maxWidth: '420px',
        textAlign: 'left',
        background: 'rgb(var(--background-secondary-color))',
        color: 'rgb(var(--text-color))',
        border: 'none',
        boxShadow: '0 16px 48px rgba(0, 0, 0, 0.1), 0 4px 12px rgba(0, 0, 0, 0.06), 0 0 0 1px rgba(var(--border-color), 0.12)',
        padding: '14px 20px',
        whiteSpace: 'pre-line'
      },
      success: {
        iconTheme: {
          primary: 'rgb(var(--primary))',
          secondary: 'rgb(var(--primary-foreground))'
        }
      },
      error: {
        iconTheme: {
          primary: 'rgb(var(--destructive))',
          secondary: 'rgb(var(--destructive-foreground))'
        }
      },
      loading: {
        iconTheme: {
          primary: 'rgb(var(--muted-foreground))',
          secondary: 'rgb(var(--muted))'
        }
      }
    }} />
    </>;
};